import { ColDef, GetRowIdParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { PRD3060_R01Dto, defaultPRD3060_R01Data } from "./Dto_PRD3060_R01";
import { usePRD3060_R01Store } from "./Store_PRD3060_R01";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = "PRD3060_R01-grid-column-state";

export const toPRD3060_R01RowKey = (row: Pick<PRD3060_R01Dto, "productResultNo" | "itemCd" | "lotNo">) =>
    `${row.productResultNo ?? ""}|${row.itemCd ?? ""}|${row.lotNo ?? ""}`;

interface Props {
    gridRef: React.RefObject<AgGridReact<PRD3060_R01Dto> | null>;
    onRowClick?: (row: PRD3060_R01Dto) => void;
}

export const Table_PRD3060_R01 = (props: Props) => {
    const { gridRef, onRowClick } = props;

    const {
        PRD3060_R01List,
        selectedPRD3060_R01List,
        setSelectedPRD3060_R01List,
        columnState,
        setColumnState,
        scrollTop,
        setScrollTop,
        setDetailScrollTop,
    } = usePRD3060_R01Store();

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const {
        paddedGridData,
        settingRowIndex,
    } = useGridSettingContent({
        gridRef,
        key: "productResultNo",
        gridData: PRD3060_R01List,
        defaultGridData: defaultPRD3060_R01Data,
        selectedRow: selectedPRD3060_R01List,
    });

    const headerNames: Record<string, string> = {
        rowIndex: "",
        productDt: "생산일자",
        itemCd: "품목코드",
        itemNm: "품명",
        itemSpec: "규격",
    };

    // 복합키 생성 함수
    const getRowIdForGrid = useCallback((params: GetRowIdParams<PRD3060_R01Dto>) => toPRD3060_R01RowKey(params.data), []);

    const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, "itemCd")(params), []);

    const handleRowClick = useCallback((event: RowClickedEvent<PRD3060_R01Dto>) => {
        if (event.node.rowPinned === "bottom") return;
        const data = event.data;
        if (!data) return;

        const PRD3060_R01RowKey = toPRD3060_R01RowKey(data);
        const isSelected = selectedPRD3060_R01List.includes(PRD3060_R01RowKey);
        if (isSelected) {
            setSelectedPRD3060_R01List([]);
        } else {
            setSelectedPRD3060_R01List([PRD3060_R01RowKey]);
            setDetailScrollTop(null);
            if (onRowClick && data.productResultNo) {
                onRowClick(data);
            }
        }
    }, [selectedPRD3060_R01List, setSelectedPRD3060_R01List, setDetailScrollTop, onRowClick]);

    const baseColumnDefs: ColDef<PRD3060_R01Dto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: "생산일자", colId: "productDt", field: "productDt", width: 110,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "productDt"),
            valueFormatter: (p) => (p.value ? formatDate(p.value) : ""),
        },
        {
            headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 130,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "itemCd"),
        },
        {
            headerName: "품명", colId: "itemNm", field: "itemNm", width: 180,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "itemNm"),
        },
        {
            headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 150,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "itemSpec"),
        },
        {
            headerName: "브랜드", colId: "brandNm", field: "brandNm", width: 100,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "brandNm"),
        },
        {
            headerName: "Lot No", colId: "lotNo", field: "lotNo", width: 150,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "lotNo"),
        },
        {
            headerName: "생산량", colId: "productQty", field: "productQty", width: 90,
            editable: false,
            type: "numericColumn",
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, "productQty"),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
        {
            headerName: "불량수량", colId: "badQty", field: "badQty", width: 90,
            editable: false,
            type: "numericColumn",
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, "badQty"),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
        {
            headerName: "출고량", colId: "acceptQty", field: "acceptQty", width: 90,
            editable: false,
            type: "numericColumn",
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, "acceptQty"),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
        {
            headerName: "비고", colId: "remark", field: "remark", width: 150,
            editable: false,
            cellStyle: (p) => getCellStyle(p, "remark"),
        },
    ], [getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PRD3060_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const rowClassRulesWithCompositeKey = useMemo(() => {
        const isSelected = (data: PRD3060_R01Dto | undefined) =>
            !!data && selectedPRD3060_R01List.includes(toPRD3060_R01RowKey(data));

        return {
            selected: (params: { node: { rowPinned?: string | null }; data?: PRD3060_R01Dto }) => {
                if (params.node.rowPinned === "bottom") return false;
                return isSelected(params.data);
            },
            hover: (params: { node: { rowPinned?: string | null }; data?: PRD3060_R01Dto }) => {
                if (params.node.rowPinned === "bottom") return false;
                return !isSelected(params.data);
            },
        };
    }, [selectedPRD3060_R01List]);

    const pinnedBottomRow = useMemo(() => [
        {
            itemCd: `${PRD3060_R01List.length}건`,
            productQty: PRD3060_R01List.reduce((s, r) => s + (Number(r.productQty) || 0), 0),
            badQty: PRD3060_R01List.reduce((s, r) => s + (Number(r.badQty) || 0), 0),
            acceptQty: PRD3060_R01List.reduce((s, r) => s + (Number(r.acceptQty) || 0), 0),
        },
    ], [PRD3060_R01List]);

    return (
        <div className="flex flex-col h-full min-h-0">
            <GridContextWrapper<PRD3060_R01Dto>
            	enableCellCalculation
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                setPaddedGridData={undefined}
                initSelected={() => setSelectedPRD3060_R01List([])}
                defaultRowData={defaultPRD3060_R01Data}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                enableDataHistory={false}
                menuItems={undefined}
            >
                <Grid_Touch<PRD3060_R01Dto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
                    onCellKeyDown={undefined}
                    rowClassRules={rowClassRulesWithCompositeKey}
                    onRowClicked={handleRowClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    setHoveredRowIndex={setHoveredRowIndex}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
};
